import Table from '../../../Components/Table/Table';
import Utils from '../../../Utils';

const FixedNumber = ({ value }) => {
    if (!value) return null;
    return <div style={{ textAlign: 'center' }}>{Number(value).toFixed(2)}</div>;
};

const DurationCell = ({ row }) => {
    const minutes = row.original.swimmingDurationMin;
    const seconds = row.original.swimmingDurationSec;
    const formattedDuration = Utils.formatDuration(minutes, seconds);

    if (!formattedDuration) return null;
    return <div style={{ textAlign: 'center' }}>{formattedDuration}</div>;
};

const columns = [
    {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => (
            <div>
                <span
                    style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor:
                            value === 'Completed'
                                ? '#d4edda'
                                : value === 'Assigned'
                                  ? '#fff3cd'
                                  : '#f8d7da',
                        color:
                            value === 'Completed'
                                ? '#155724'
                                : value === 'Assigned'
                                  ? '#856404'
                                  : '#721c24',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                    }}
                >
                    {value || 'Unknown'}
                </span>
            </div>
        )
    },
    {
        Header: 'Date',
        accessor: 'workoutStart',
        Cell: ({ value }) => <div>{new Date(value).toLocaleDateString('en-GB')}</div>
    },
    {
        Header: 'Duration',
        accessor: 'trainingDuration',
        Cell: DurationCell
    },
    {
        id: 'volume',
        Header: () => <div style={{ textAlign: 'center' }}>Volume</div>,
        accessor: 'volume',
        Cell: FixedNumber
    },
    {
        id: 'css',
        Header: () => <div style={{ textAlign: 'center' }}>CSS</div>,
        accessor: 'css',
        Cell: FixedNumber
    },
    {
        id: 'ftp',
        Header: () => <div style={{ textAlign: 'center' }}>FTP</div>,
        accessor: 'ftp',
        Cell: FixedNumber
    },
    {
        id: 'nss',
        Header: () => <div style={{ textAlign: 'center' }}>NSS</div>,
        accessor: 'nss',
        Cell: FixedNumber
    },
    {
        id: 'if',
        Header: () => <div style={{ textAlign: 'center' }}>IF</div>,
        accessor: 'if',
        Cell: FixedNumber
    },
    {
        id: 'tl',
        Header: () => <div style={{ textAlign: 'center' }}>TL</div>,
        accessor: 'tl',
        Cell: FixedNumber
    },
    {
        id: 'tss',
        Header: () => <div style={{ textAlign: 'center' }}>TSS</div>,
        accessor: 'tss',
        Cell: FixedNumber
    },
    {
        id: 'atl',
        Header: () => <div style={{ textAlign: 'center' }}>ATL</div>,
        accessor: 'atl',
        Cell: FixedNumber
    },
    {
        id: 'ctl',
        Header: () => <div style={{ textAlign: 'center' }}>CTL</div>,
        accessor: 'ctl',
        Cell: FixedNumber
    },
    {
        id: 'tsb',
        Header: () => <div style={{ textAlign: 'center' }}>TSB</div>,
        accessor: 'tsb',
        Cell: FixedNumber
    },
    {
        id: 'acwr',
        Header: () => <div style={{ textAlign: 'center' }}>ACWR</div>,
        accessor: 'acwr',
        Cell: FixedNumber
    }
];

export const Glossary = () => (
    <div
        style={{
            width: '100%',
            padding: 'var(--space-xl)',
            background: 'var(--color-surface-elevated)',
            borderRadius: 'var(--radius-lg)',
            boxSizing: 'border-box'
        }}
    >
        <h3
            style={{
                marginTop: 0,
                marginBottom: 'var(--space-lg)',
                color: 'var(--color-text-primary)',
                fontSize: 'var(--font-size-xl)',
                fontWeight: '700'
            }}
        >
            Glossary
        </h3>
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: 'var(--space-md)',
                fontSize: 'var(--font-size-sm)'
            }}
        >
            <div style={{ padding: 'var(--space-sm)' }}>
                <strong style={{ color: 'var(--color-accent)' }}>CSS</strong>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                    {' '}
                    - Critical Swim Speed: Maximum sustainable swimming speed
                </span>
            </div>
            <div style={{ padding: 'var(--space-sm)' }}>
                <strong style={{ color: 'var(--color-accent)' }}>FTP</strong>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                    {' '}
                    - Functional Threshold Pace: Pace you can maintain for ~60 minutes
                </span>
            </div>
            <div style={{ padding: 'var(--space-sm)' }}>
                <strong style={{ color: 'var(--color-accent)' }}>NSS</strong>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                    {' '}
                    - Normalized Swim Speed: Adjusted average speed accounting for intensity
                </span>
            </div>
            <div style={{ padding: 'var(--space-sm)' }}>
                <strong style={{ color: 'var(--color-accent)' }}>IF</strong>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                    {' '}
                    - Intensity Factor: Ratio of NSS to FTP (workout intensity)
                </span>
            </div>
            <div style={{ padding: 'var(--space-sm)' }}>
                <strong style={{ color: 'var(--color-accent)' }}>TL</strong>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                    {' '}
                    - Training Load: Overall training stress from a workout
                </span>
            </div>
            <div style={{ padding: 'var(--space-sm)' }}>
                <strong style={{ color: 'var(--color-accent)' }}>TSS</strong>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                    {' '}
                    - Training Stress Score: Quantified training stress (duration × intensity)
                </span>
            </div>
            <div style={{ padding: 'var(--space-sm)' }}>
                <strong style={{ color: 'var(--color-accent)' }}>ATL</strong>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                    {' '}
                    - Acute Training Load: Short-term fatigue (7-day average)
                </span>
            </div>
            <div style={{ padding: 'var(--space-sm)' }}>
                <strong style={{ color: 'var(--color-accent)' }}>CTL</strong>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                    {' '}
                    - Chronic Training Load: Long-term fitness (42-day average)
                </span>
            </div>
            <div style={{ padding: 'var(--space-sm)' }}>
                <strong style={{ color: 'var(--color-accent)' }}>TSB</strong>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                    {' '}
                    - Training Stress Balance: Readiness to perform (CTL - ATL)
                </span>
            </div>
            <div style={{ padding: 'var(--space-sm)' }}>
                <strong style={{ color: 'var(--color-accent)' }}>ACWR</strong>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                    {' '}
                    - Acute:Chronic Workload Ratio: Injury risk indicator (ATL ÷ CTL)
                </span>
            </div>
        </div>
    </div>
);

export default function PerformanceTable({ data, dateRange, isValidDateRange }) {
    const tableData = (() => {
        let workouts = data;
        let startDate;
        let endDate;
        if (isValidDateRange(dateRange.startDate, dateRange.endDate)) {
            startDate = new Date(dateRange.startDate);
            endDate = new Date(dateRange.endDate);
            endDate.setHours(23, 59, 59);
            workouts = data.filter(workout => {
                const workoutDate = new Date(workout.workoutStart);
                return workoutDate >= startDate && workoutDate <= endDate;
            });
        }
        // Sort by date (most recent first) and then by status (Completed before Assigned)
        return workouts.sort((a, b) => {
            const dateA = new Date(a.workoutStart);
            const dateB = new Date(b.workoutStart);
            if (dateB.getTime() !== dateA.getTime()) {
                return dateB.getTime() - dateA.getTime();
            }
            // If same date, show Completed before Assigned
            if (a.status === 'Completed' && b.status === 'Assigned') return -1;
            if (a.status === 'Assigned' && b.status === 'Completed') return 1;
            return 0;
        });
    })();

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
            }}
        >
            <Table columns={columns} data={tableData} />
        </div>
    );
}
